/**
 * useFolio — React Query hooks for the Folio payment system.
 *
 * Provides:
 *   useFolio(bookingId)         — fetch folio with charges + balance
 *   useFolioPayments(folioId)   — fetch payment list for a folio
 *   useRecordFolioPayment()     — mutation: record a payment
 *   useVoidFolioCharge()        — mutation: void a charge line
 *   useCloseFolio()             — mutation: close a folio
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import {
  getFolioByBookingId,
  getFolioPayments,
  recordFolioPayment,
  voidFolioCharge,
  closeFolio,
} from '../services/bookingService';
import type { RecordFolioPaymentInput } from '../types';

const FOLIO_KEYS = {
  folio: (bookingId: string) => ['folio', bookingId] as const,
  payments: (folioId: string) => ['folio-payments', folioId] as const,
};

/** Fetch the Folio for a booking (includes charges + balance). */
export const useFolio = (bookingId: string) =>
  useQuery({
    queryKey: FOLIO_KEYS.folio(bookingId),
    queryFn: () => getFolioByBookingId(bookingId),
    enabled: !!bookingId,
    staleTime: 30_000,
  });

/** Fetch payment list for an open folio. */
export const useFolioPayments = (folioId: string | undefined) =>
  useQuery({
    queryKey: FOLIO_KEYS.payments(folioId ?? ''),
    queryFn: () => getFolioPayments(folioId!),
    enabled: !!folioId,
    staleTime: 30_000,
  });

/** Record a payment against a folio. */
export const useRecordFolioPayment = (bookingId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: RecordFolioPaymentInput) => recordFolioPayment(input),
    onSuccess: (payment) => {
      queryClient.invalidateQueries({ queryKey: FOLIO_KEYS.folio(bookingId) });
      queryClient.invalidateQueries({ queryKey: FOLIO_KEYS.payments(payment.folioId ?? '') });
      notify.success(t('folio.paymentRecorded'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('folio.paymentError') });
    },
  });
};

/** Void a charge line. */
export const useVoidFolioCharge = (bookingId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ chargeId, reason }: { chargeId: string; reason: string }) =>
      voidFolioCharge(chargeId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLIO_KEYS.folio(bookingId) });
      notify.success(t('folio.chargeVoided'));
    },
    onError: (error: Error) => {
      notify.error(error.message);
    },
  });
};

/** Close a folio (balance must be 0). */
export const useCloseFolio = (bookingId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (folioId: string) => closeFolio(folioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLIO_KEYS.folio(bookingId) });
      notify.success(t('folio.closed'));
    },
    onError: (error: Error) => {
      notify.error(error.message);
    },
  });
};
